import type { ReactNode } from 'react';

/**
 * The SideNav's rules, kept out of the component so they can be tested without
 * rendering it: no JSX, no MUI, no router here.
 */

export interface NavItem {
  label: string;
  path?: string;
  icon: ReactNode;
  disabled?: boolean;
  comingSoon?: boolean;
  tooltip?: string;
  /** A live count shown beside the label (#717), read by the SideNav. */
  countKey?: NavCountKey;
}

/**
 * The live counts a nav row can carry: open feedbacks (#717), client imports to
 * handle (#748), Profile Cleaning issues (#753).
 */
export type NavCountKey = 'openFeedbacks' | 'pendingClientImports' | 'cleaningIssues';

/** What a count is of, for the badge's accessible label and tooltip ("3 open"). */
export const NAV_COUNT_WORDING: Record<NavCountKey, string> = {
  openFeedbacks: 'open',
  pendingClientImports: 'client imports to handle',
  cleaningIssues: 'issues',
};

export interface NavSection {
  title?: string;
  items: NavItem[];
}

/**
 * Prefix match on a segment boundary, so `/admin/api` does not light up on
 * `/admin/apifoo`.
 */
const matchesPrefix = (path: string, pathname: string) =>
  pathname === path || pathname.startsWith(`${path}/`);

/**
 * Active detection: exact for client, prefix for admin so detail/edit
 * sub-routes keep their parent lit (`/admin/collection/:id` → "Collection").
 *
 * Most specific wins: when one nav item's path is a prefix of another's, only
 * the deepest match is active: `/admin/profiles/deactivated` lights up
 * "Deactivated Profiles" alone, not "Profiles" as well.
 */
export function isNavPathActive(
  path: string,
  { pathname, navPaths, prefix }: { pathname: string; navPaths: string[]; prefix: boolean },
): boolean {
  if (!prefix) return pathname === path;
  if (!matchesPrefix(path, pathname)) return false;
  return !navPaths.some((other) => other.length > path.length && matchesPrefix(other, pathname));
}

/**
 * What a row's count badge says: nothing at zero or before the count is known
 * (no badge rather than a misleading 0), the number up to 99, then `99+` so the
 * badge keeps its width in a 248px nav.
 */
export function navCountLabel(count: number | null | undefined): string | null {
  if (count === null || count === undefined || count <= 0) return null;
  return count > 99 ? '99+' : String(count);
}
